import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PermissionService } from '../../../services/permission.service';
import { EmployeesService } from '../../../employees/employees.service';
import { ModuleTree, DesignationPermissionMatrix, ResourcePermissions } from '../../../common/models/permission.model';
import { Designation } from '../../../employees/employees.model';
import { finalize } from 'rxjs';
import { SnackbarService } from '../../../services/snackbar.service';
import { SnackbarAlertStatus } from '../../../utils';
import { ConfirmationModalComponent } from '../../../common/components/confirmation-modal/confirmation-modal.component';

// Column labels that read better than the raw action name.
const ACTION_LABELS: { [key: string]: string } = {
  view: 'Access',
  admin: 'Admin Site',
  app: 'Mobile App',
};

// Login access is granted per device, not per CRUD action: admin site and mobile app are
// independent switches, so this module gets no bulk selectors.
const AUTH_ACTIONS = ['admin', 'app'];

// Column order for the permission matrix. Anything unlisted is appended alphabetically.
const ACTION_ORDER = ['view', 'create', 'edit', 'delete', 'approve', 'ship', 'export', 'admin', 'app'];

@Component({
  selector: 'app-role-management',
  standalone: false,
  templateUrl: './role-management.component.html',
  styleUrl: './role-management.component.scss',
})
export class RoleManagementComponent implements OnInit {
  protected designations: Designation[] = [];
  protected filteredDesignations: Designation[] = [];
  protected designationSearch: string = '';

  protected permissionTree: ModuleTree[] = [];
  protected filteredTree: ModuleTree[] = [];
  protected permissionSearch: string = '';

  protected selectedDesignationId: string | null = null;
  protected matrix: DesignationPermissionMatrix | null = null;
  protected copySourceId: string = '';

  protected isLoadingTree: boolean = false;
  protected isLoadingDesignations: boolean = false;
  protected isLoadingMatrix: boolean = false;
  protected isSaving: boolean = false;
  protected isCopying: boolean = false;

  // Live selection, and the snapshot it is compared against to find unsaved edits.
  protected selectedPermissionIds: Set<string> = new Set();
  private _savedPermissionIds: Set<string> = new Set();
  protected dirtyCount: number = 0;

  protected expandedModules: Set<string> = new Set();
  protected totalPermissionCount: number = 0;

  // Ordered action column list per module, built once when the tree loads.
  private _moduleActions: Map<string, string[]> = new Map();
  private _moduleResourceCount: Map<string, number> = new Map();

  // Admin site login gates everything else: a designation without it never reaches the admin panel,
  // so the remaining modules stay read-only until it is granted.
  private _adminAccessPermissionIds: string[] = [];

  constructor(
    private _permissionService: PermissionService,
    private _employeesService: EmployeesService,
    private _snackbarService: SnackbarService,
    private _modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this._loadDesignations();
    this._loadPermissionTree();
  }

  // ─── Loading ────────────────────────────────────────────────────────────────

  private _loadDesignations(): void {
    this.isLoadingDesignations = true;
    this._employeesService
      .getDesignations()
      .pipe(finalize(() => (this.isLoadingDesignations = false)))
      .subscribe({
        next: (data: any) => {
          // Handle both array and paginated responses
          this.designations = Array.isArray(data) ? data : data.results || [];
          this.filterDesignations();

          if (this.designations.length > 0) {
            this._selectDesignation(this.designations[0].id);
          }
        },
      });
  }

  private _loadPermissionTree(): void {
    this.isLoadingTree = true;
    this._permissionService
      .getPermissionTree()
      .pipe(finalize(() => (this.isLoadingTree = false)))
      .subscribe({
        next: tree => {
          this.permissionTree = tree;
          this.totalPermissionCount = 0;
          this._adminAccessPermissionIds = [];

          this.permissionTree.forEach(module => {
            const actions = new Set<string>();
            module.resources.forEach(resource => {
              resource.actions.forEach(action => actions.add(action.action));
              this.totalPermissionCount += resource.actions.length;
            });
            this._moduleActions.set(module.name, this._orderActions(Array.from(actions)));
            this._moduleResourceCount.set(module.name, module.resources.length);

            if (Array.from(actions).every(action => AUTH_ACTIONS.includes(action))) {
              module.resources.forEach(resource => {
                const admin = resource.actions.find(a => a.action === 'admin');
                if (admin) this._adminAccessPermissionIds.push(admin.permission_id);
              });
            }
          });

          // Modules start collapsed: the count badge on each header says what is enabled, so an
          // admin opens only the module they came to change. Login access is the exception, since
          // it gates every other module and is the first thing to set on a new designation.
          this.expandedModules.clear();
          this.permissionTree
            .filter(module => this.isLoginAccessModule(module.name))
            .forEach(module => this.expandedModules.add(module.name));

          this.filterPermissions();
          this._syncSelectedPermissions();
        },
      });
  }

  private _orderActions(actions: string[]): string[] {
    return actions.sort((a, b) => {
      const ai = ACTION_ORDER.indexOf(a);
      const bi = ACTION_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }

  // ─── Designation selection ──────────────────────────────────────────────────

  protected filterDesignations(): void {
    const term = this.designationSearch.trim().toLowerCase();
    this.filteredDesignations = term
      ? this.designations.filter(d => d.title?.toLowerCase().includes(term))
      : [...this.designations];
  }

  protected onSelectDesignation(id: string): void {
    if (id === this.selectedDesignationId) return;

    if (this.dirtyCount > 0) {
      const modalRef = this._modalService.open(ConfirmationModalComponent, { centered: true, backdrop: 'static' });
      modalRef.componentInstance.title = 'Discard unsaved changes?';
      modalRef.componentInstance.message = `You have ${this.dirtyCount} unsaved permission change(s) for ${this.selectedDesignationTitle}.`;
      modalRef.componentInstance.warning = 'Switching designation will discard them.';
      modalRef.componentInstance.confirmText = 'Discard & Switch';
      modalRef.componentInstance.confirmClass = 'btn-danger';
      modalRef.closed.subscribe(() => this._selectDesignation(id));
      return;
    }

    this._selectDesignation(id);
  }

  private _selectDesignation(id: string): void {
    this.selectedDesignationId = id;
    this.copySourceId = '';
    this.isLoadingMatrix = true;
    this._permissionService
      .getDesignationMatrix(id)
      .pipe(finalize(() => (this.isLoadingMatrix = false)))
      .subscribe({
        next: matrix => {
          this.matrix = matrix;
          this._syncSelectedPermissions();
        },
      });
  }

  protected get selectedDesignationTitle(): string {
    return this.designations.find(d => d.id === this.selectedDesignationId)?.title || 'designation';
  }

  /** Rebuilds the live selection from the loaded matrix and resets the dirty baseline. */
  private _syncSelectedPermissions(): void {
    this.selectedPermissionIds = this._idsFromMatrix(this.matrix);
    this._savedPermissionIds = new Set(this.selectedPermissionIds);
    this.dirtyCount = 0;
  }

  private _idsFromMatrix(matrix: DesignationPermissionMatrix | null): Set<string> {
    const ids = new Set<string>();
    if (!matrix) return ids;

    this.permissionTree.forEach(module => {
      module.resources.forEach(resource => {
        resource.actions.forEach(action => {
          const code = `${module.name}.${resource.name}.${action.action}`;
          if (matrix.permissions[code]) ids.add(action.permission_id);
        });
      });
    });
    return ids;
  }

  // ─── Search & expansion ─────────────────────────────────────────────────────

  protected filterPermissions(): void {
    const term = this.permissionSearch.trim().toLowerCase();

    if (!term) {
      this.filteredTree = this.permissionTree;
      return;
    }

    this.filteredTree = this.permissionTree
      .map(module => {
        const moduleMatches = (module.label || module.name).toLowerCase().includes(term);
        const resources = module.resources.filter(resource => {
          const label = this.getResourceDisplayLabel(module, resource);
          return (
            moduleMatches ||
            label.toLowerCase().includes(term) ||
            `${module.name}.${resource.name}`.toLowerCase().includes(term) ||
            resource.actions.some(a => a.action.toLowerCase().includes(term))
          );
        });
        return { ...module, resources } as ModuleTree;
      })
      .filter(module => module.resources.length > 0);

    // A search is only useful if you can see the hits.
    this.filteredTree.forEach(module => this.expandedModules.add(module.name));
  }

  protected clearPermissionSearch(): void {
    this.permissionSearch = '';
    this.filterPermissions();
  }

  protected toggleModule(moduleName: string): void {
    if (this.expandedModules.has(moduleName)) {
      this.expandedModules.delete(moduleName);
    } else {
      this.expandedModules.add(moduleName);
    }
  }

  protected isModuleExpanded(moduleName: string): boolean {
    return this.expandedModules.has(moduleName);
  }

  protected expandAll(): void {
    this.permissionTree.forEach(module => this.expandedModules.add(module.name));
  }

  protected collapseAll(): void {
    this.expandedModules.clear();
  }

  // ─── Matrix reads ───────────────────────────────────────────────────────────

  protected getModuleActions(moduleName: string): string[] {
    return this._moduleActions.get(moduleName) || [];
  }

  /**
   * True while the designation can sign in to the admin site, and true as a fallback when the tree
   * carries no admin-login permission at all: an unknown gate must not lock the whole screen.
   */
  protected get isAdminAccessGranted(): boolean {
    return (
      this._adminAccessPermissionIds.length === 0 ||
      this._adminAccessPermissionIds.some(id => this.selectedPermissionIds.has(id))
    );
  }

  /** Everything outside login access is read-only until admin site login is granted. */
  protected isModuleLocked(moduleName: string): boolean {
    return !this.isAdminAccessGranted && !this.isLoginAccessModule(moduleName);
  }

  /** Admin site and mobile app login, where every checkbox is a deliberate one-off grant. */
  protected isLoginAccessModule(moduleName: string): boolean {
    const actions = this.getModuleActions(moduleName);
    return actions.length > 0 && actions.every(action => AUTH_ACTIONS.includes(action));
  }

  /**
   * Column and row select-all boxes earn their place only in a CRUD module with several action
   * columns. In a single-action module they would each toggle the one cell already in view, and
   * login access is granted device by device.
   */
  protected showBulkSelectors(moduleName: string): boolean {
    return this.getModuleActions(moduleName).length > 1 && !this.isLoginAccessModule(moduleName);
  }

  /** The permission for one cell, or undefined when this resource has no such action. */
  protected getPermissionId(resource: ResourcePermissions, action: string): string | undefined {
    return resource.actions.find(a => a.action === action)?.permission_id;
  }

  protected isPermissionChecked(permissionId: string): boolean {
    return this.selectedPermissionIds.has(permissionId);
  }

  protected getModuleSelectedCount(module: ModuleTree): number {
    return module.resources.reduce(
      (sum, resource) => sum + resource.actions.filter(a => this.selectedPermissionIds.has(a.permission_id)).length,
      0
    );
  }

  protected getModuleTotalCount(module: ModuleTree): number {
    return module.resources.reduce((sum, resource) => sum + resource.actions.length, 0);
  }

  protected isColumnFullyChecked(module: ModuleTree, action: string): boolean {
    const ids = this._columnIds(module, action);
    return ids.length > 0 && ids.every(id => this.selectedPermissionIds.has(id));
  }

  protected isColumnPartiallyChecked(module: ModuleTree, action: string): boolean {
    const ids = this._columnIds(module, action);
    const checked = ids.filter(id => this.selectedPermissionIds.has(id)).length;
    return checked > 0 && checked < ids.length;
  }

  protected isResourceFullyChecked(resource: ResourcePermissions): boolean {
    return resource.actions.length > 0 && resource.actions.every(a => this.selectedPermissionIds.has(a.permission_id));
  }

  protected isResourcePartiallyChecked(resource: ResourcePermissions): boolean {
    const checked = resource.actions.filter(a => this.selectedPermissionIds.has(a.permission_id)).length;
    return checked > 0 && checked < resource.actions.length;
  }

  private _columnIds(module: ModuleTree, action: string): string[] {
    return module.resources.map(resource => this.getPermissionId(resource, action)).filter((id): id is string => !!id);
  }

  // ─── Matrix writes ──────────────────────────────────────────────────────────

  protected togglePermission(permissionId: string): void {
    this._setMany([permissionId], !this.selectedPermissionIds.has(permissionId));
  }

  protected toggleResource(resource: ResourcePermissions): void {
    const target = !this.isResourceFullyChecked(resource);
    this._setMany(
      resource.actions.map(a => a.permission_id),
      target
    );
  }

  protected toggleColumn(module: ModuleTree, action: string): void {
    const target = !this.isColumnFullyChecked(module, action);
    this._setMany(this._columnIds(module, action), target);
  }

  /** Select all / clear all act on what the filter currently shows, so a search narrows the blast radius. */
  protected selectAll(): void {
    this._setMany(this._visiblePermissionIds(), true);
  }

  protected clearAll(): void {
    this._setMany(this._visiblePermissionIds(), false);
  }

  private _visiblePermissionIds(): string[] {
    return this.filteredTree.flatMap(module =>
      module.resources.flatMap(resource => resource.actions.map(a => a.permission_id))
    );
  }

  protected resetChanges(): void {
    this.selectedPermissionIds = new Set(this._savedPermissionIds);
    this.dirtyCount = 0;
  }

  private _setMany(permissionIds: string[], checked: boolean): void {
    permissionIds.forEach(id => {
      if (checked) {
        this.selectedPermissionIds.add(id);
      } else {
        this.selectedPermissionIds.delete(id);
      }
    });
    this._recomputeDirty();
  }

  private _recomputeDirty(): void {
    let count = 0;
    this.selectedPermissionIds.forEach(id => {
      if (!this._savedPermissionIds.has(id)) count++;
    });
    this._savedPermissionIds.forEach(id => {
      if (!this.selectedPermissionIds.has(id)) count++;
    });
    this.dirtyCount = count;
  }

  // ─── Copy from another designation ──────────────────────────────────────────

  protected get copySourceOptions(): Designation[] {
    return this.designations.filter(d => d.id !== this.selectedDesignationId);
  }

  protected copyFromDesignation(): void {
    if (!this.copySourceId) return;

    const sourceTitle = this.designations.find(d => d.id === this.copySourceId)?.title || 'designation';
    this.isCopying = true;
    this._permissionService
      .getDesignationMatrix(this.copySourceId)
      .pipe(finalize(() => (this.isCopying = false)))
      .subscribe({
        next: matrix => {
          this.selectedPermissionIds = this._idsFromMatrix(matrix);
          this._recomputeDirty();
          this.copySourceId = '';
          this._snackbarService.show(
            `Copied permissions from ${sourceTitle}. Review and save to apply.`,
            SnackbarAlertStatus.SUCCESS
          );
        },
        error: () => {
          this._snackbarService.show('Failed to load permissions to copy', SnackbarAlertStatus.ERROR);
        },
      });
  }

  // ─── Labels ─────────────────────────────────────────────────────────────────

  /**
   * The login module's one row spans both device columns, so naming it after either device
   * (the API calls it 'Admin Site Login Access') reads as a contradiction against the columns.
   */
  protected getResourceDisplayLabel(module: ModuleTree, resource: ResourcePermissions): string {
    if (this.isLoginAccessModule(module.name) && this._moduleResourceCount.get(module.name) === 1) {
      return 'Device Login Access';
    }
    return this.formatResourceLabel(module.label || module.name, resource.label || resource.name);
  }

  protected formatResourceLabel(moduleLabel: string, resourceLabel: string): string {
    let cleanResourceLabel = resourceLabel;

    // If resource label perfectly starts with the module label (e.g. 'Inventory Sidebar' under 'Inventory')
    if (moduleLabel && cleanResourceLabel.toLowerCase().startsWith(moduleLabel.toLowerCase())) {
      cleanResourceLabel = cleanResourceLabel.substring(moduleLabel.length).trim();
    }

    // A resource named after its own module strips to nothing: 'Orders' under 'Orders' is the
    // orders list itself, and the row rendered with a blank name.
    if (!cleanResourceLabel) {
      return resourceLabel;
    }

    if (cleanResourceLabel.toLowerCase() === 'sidebar') {
      return 'Main Sidebar Navigation';
    }

    return cleanResourceLabel;
  }

  protected getActionLabel(action: string): string {
    return ACTION_LABELS[action] || action;
  }

  // ─── Save ───────────────────────────────────────────────────────────────────

  protected savePermissions(): void {
    if (!this.selectedDesignationId || this.dirtyCount === 0) return;

    this.isSaving = true;
    this._permissionService
      .updateDesignationPermissions(this.selectedDesignationId, Array.from(this.selectedPermissionIds))
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => {
          this._savedPermissionIds = new Set(this.selectedPermissionIds);
          this.dirtyCount = 0;
          this._snackbarService.show('Permissions updated successfully', SnackbarAlertStatus.SUCCESS);
        },
        error: () => {
          this._snackbarService.show('Failed to update permissions', SnackbarAlertStatus.ERROR);
        },
      });
  }
}
